import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  CloseProvisionRequestInput,
  CreateProvisionRequestInput,
  CreateProvisionRequestMessageInput,
  CreateProvisionRequestOutput,
  GetMatchcodesOutput,
  GetProvisionRequestMessagesOutput,
  GetProvisionRequestOutput,
  GetProvisionRequestsInput,
  GetProvisionRequestsOutput,
} from './provision-request-api.types';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { ProvisionRequest } from './provision-request-api.class';
import { NzMessageService } from 'ng-zorro-antd/message';

@Injectable({ providedIn: 'root' })
export class ProvisionRequestApiService {
  private resource = `${environment.api.avs}/versorgungsanfrage`;

  constructor(
    private httpClient: HttpClient,
    private message: NzMessageService,
  ) {}

  getAllProvisionRequests(
    input: GetProvisionRequestsInput,
  ): Observable<GetProvisionRequestOutput[]> {
    const url = `${this.resource}`;
    const params = new HttpParams({ fromObject: input });

    return this.httpClient
      .get<GetProvisionRequestsOutput>(url, { params })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of([]); // Return empty array instead of throwing an 404 error
          } else {
            this.message.error(
              'Etwas beim Laden der Versorgungsanfragen ist schiefgelaufen',
            );
            return of([]);
          }
        }),
      );
  }

  async createProvisionRequest(
    input: CreateProvisionRequestInput,
  ): Promise<CreateProvisionRequestOutput> {
    const url = `${this.resource}`;
    return await firstValueFrom(
      this.httpClient.post<CreateProvisionRequestOutput>(url, input, {}),
    );
  }

  getProvisionRequest(id: number): Observable<ProvisionRequest> {
    const url = `${this.resource}/${id}`;

    return this.httpClient
      .get<GetProvisionRequestOutput>(url, {})
      .pipe(map((result) => new ProvisionRequest(result)));
  }

  async closeProvisionRequest(
    id: number,
    input: CloseProvisionRequestInput,
  ): Promise<any> {
    const url = `${this.resource}/${id}/beenden`;

    return await firstValueFrom(
      this.httpClient.patch(url, {}, { params: input }),
    );
  }

  getProvisionRequestMessages(
    id: number,
  ): Observable<GetProvisionRequestMessagesOutput> {
    const url = `${this.resource}/${id}/nachrichten`;

    return this.httpClient.get<GetProvisionRequestMessagesOutput>(url, {});
  }

  createProvisionRequestMessage(
    id: number,
    input: CreateProvisionRequestMessageInput,
  ): Observable<any> {
    const url = `${this.resource}/${id}/nachrichten`;

    return this.httpClient.post(url, input);
  }

  async getClothingEquipment(): Promise<GetMatchcodesOutput[]> {
    const url = `${environment.api.avs}/Matchcode/kleidungsausstattung`;

    return await firstValueFrom(
      this.httpClient.get<GetMatchcodesOutput[]>(url),
    );
  }

  async getClothingTypes(): Promise<GetMatchcodesOutput[]> {
    const url = `${environment.api.avs}/Matchcode/kleidungsart`;

    return await firstValueFrom(
      this.httpClient.get<GetMatchcodesOutput[]>(url),
    );
  }
}
